/**
 * The diff rendering contract (`docs/spec/revisions.md` §5).
 *
 * Two revisions in, one HTML fragment out: the "after" text with the changes
 * woven into it. Word-level inside a block, whole-block when a block only
 * exists on one side, and deletions always emitted before the insertion that
 * replaced them. The function is pure and deterministic — no dates, no ids, no
 * locale — which is what makes it unit-testable and what §5.6 requires.
 *
 * We diff the markdown source of the two revisions rather than a parsed model,
 * so the fragment shows the text as written. That is the reduction this version
 * makes; the contract's class names, nesting and ordering are all as specified,
 * so a richer diff can replace the internals without moving the output.
 */

export interface DiffInput {
  beforeTitle: string;
  afterTitle: string;
  before: string;
  after: string;
}

/** Above this many tokens a block is compared whole; the DP would not pay. */
const MAX_TOKENS = 1500;

export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

/**
 * Runs of word characters, runs of punctuation, and runs of whitespace — with
 * whitespace attached to the token before it, so word boundaries stay natural
 * and a changed word does not drag its neighbour's space into the markup.
 */
export function tokenize(text: string): string[] {
  const runs = text.match(/\s+|[\p{L}\p{N}_]+|[^\s\p{L}\p{N}_]+/gu) ?? [];
  const tokens: string[] = [];
  for (const run of runs) {
    const isSpace = /^\s+$/u.test(run);
    const last = tokens.length - 1;
    if (isSpace && last >= 0) {
      tokens[last] = `${tokens[last] ?? ""}${run}`;
    } else {
      tokens.push(run);
    }
  }
  return tokens;
}

export type OpKind = "equal" | "delete" | "insert";

export interface Op<T> {
  kind: OpKind;
  values: T[];
}

/**
 * Longest-common-subsequence diff over two sequences, returned as runs. The
 * table is filled from the end so the walk forward produces deletions before
 * insertions at every divergence, which is the order the contract fixes.
 */
export function diffSequences<T>(before: readonly T[], after: readonly T[]): Op<T>[] {
  const rows = before.length;
  const columns = after.length;
  const table: number[][] = Array.from({ length: rows + 1 }, () =>
    new Array<number>(columns + 1).fill(0),
  );
  for (let row = rows - 1; row >= 0; row -= 1) {
    for (let column = columns - 1; column >= 0; column -= 1) {
      const current = table[row] ?? [];
      const next = table[row + 1] ?? [];
      current[column] =
        before[row] === after[column]
          ? (next[column + 1] ?? 0) + 1
          : Math.max(next[column] ?? 0, current[column + 1] ?? 0);
    }
  }

  const ops: Op<T>[] = [];
  const push = (kind: OpKind, value: T): void => {
    const last = ops[ops.length - 1];
    if (last?.kind === kind) last.values.push(value);
    else ops.push({ kind, values: [value] });
  };

  let row = 0;
  let column = 0;
  while (row < rows && column < columns) {
    if (before[row] === after[column]) {
      push("equal", before[row] as T);
      row += 1;
      column += 1;
    } else if ((table[row + 1]?.[column] ?? 0) >= (table[row]?.[column + 1] ?? 0)) {
      push("delete", before[row] as T);
      row += 1;
    } else {
      push("insert", after[column] as T);
      column += 1;
    }
  }
  while (row < rows) {
    push("delete", before[row] as T);
    row += 1;
  }
  while (column < columns) {
    push("insert", after[column] as T);
    column += 1;
  }
  return ops;
}

/**
 * The inline half of the contract (§5.2): word-level `<ins>`/`<del>`, adjacent
 * runs merged, empty elements never emitted, deletion before insertion.
 */
export function diffInline(before: string, after: string): string {
  if (before === after) return escapeHtml(after);

  const beforeTokens = tokenize(before);
  const afterTokens = tokenize(after);
  if (beforeTokens.length > MAX_TOKENS || afterTokens.length > MAX_TOKENS) {
    return `${wrap("del", before)}${wrap("ins", after)}`;
  }

  let html = "";
  const ops = diffSequences(beforeTokens, afterTokens);
  for (let index = 0; index < ops.length; index += 1) {
    const op = ops[index];
    if (op === undefined) continue;
    const text = op.values.join("");
    if (op.kind === "equal") {
      html += escapeHtml(text);
      continue;
    }
    if (op.kind === "insert") {
      html += wrap("ins", text);
      continue;
    }
    // A deletion immediately followed by an insertion is one replacement, and
    // the pair is emitted in that order.
    const next = ops[index + 1];
    html += wrap("del", text);
    if (next?.kind === "insert") {
      html += wrap("ins", next.values.join(""));
      index += 1;
    }
  }
  return html;
}

function wrap(kind: "ins" | "del", text: string): string {
  if (text === "") return "";
  return `<${kind} class="diff-${kind}">${escapeHtml(text)}</${kind}>`;
}

/** Blocks are runs of lines separated by a blank line; blank runs are dropped. */
export function splitBlocks(text: string): string[] {
  return text
    .replace(/\r\n/gu, "\n")
    .split(/\n{2,}/u)
    .map((block) => block.replace(/\s+$/u, ""))
    .filter((block) => block !== "");
}

interface BlockChange {
  kind: "equal" | "changed" | "inserted" | "deleted";
  before?: string;
  after?: string;
}

/**
 * Pairs up a deletion run with the insertion run that follows it, so a block
 * that was edited reads as an edit rather than as a removal next to an
 * unrelated addition. Whatever is left over is a pure insertion or deletion.
 */
export function diffBlocks(before: readonly string[], after: readonly string[]): BlockChange[] {
  const ops = diffSequences(before, after);
  const changes: BlockChange[] = [];

  for (let index = 0; index < ops.length; index += 1) {
    const op = ops[index];
    if (op === undefined) continue;
    if (op.kind === "equal") {
      for (const block of op.values) changes.push({ kind: "equal", after: block });
      continue;
    }
    if (op.kind === "insert") {
      for (const block of op.values) changes.push({ kind: "inserted", after: block });
      continue;
    }

    const next = ops[index + 1];
    if (next?.kind !== "insert") {
      for (const block of op.values) changes.push({ kind: "deleted", before: block });
      continue;
    }
    index += 1;
    const paired = Math.min(op.values.length, next.values.length);
    for (let position = 0; position < paired; position += 1) {
      changes.push({
        kind: "changed",
        before: op.values[position] ?? "",
        after: next.values[position] ?? "",
      });
    }
    for (let position = paired; position < op.values.length; position += 1) {
      changes.push({ kind: "deleted", before: op.values[position] ?? "" });
    }
    for (let position = paired; position < next.values.length; position += 1) {
      changes.push({ kind: "inserted", after: next.values[position] ?? "" });
    }
  }
  return changes;
}

/**
 * The whole fragment: the title first, then every block of the "after" text in
 * order with deleted blocks rendered in place (§5.3).
 */
export function diffDocuments(input: DiffInput): string {
  const title = `<h1 class="diff-title">${diffInline(input.beforeTitle, input.afterTitle)}</h1>`;
  const blocks = diffBlocks(splitBlocks(input.before), splitBlocks(input.after))
    .map((change) => {
      switch (change.kind) {
        case "equal":
          return `<p class="diff-block">${escapeHtml(change.after ?? "")}</p>`;
        case "changed":
          return `<p class="diff-block">${diffInline(change.before ?? "", change.after ?? "")}</p>`;
        case "inserted":
          return `<p class="diff-ins-block">${escapeHtml(change.after ?? "")}</p>`;
        case "deleted":
          return `<p class="diff-del-block">${escapeHtml(change.before ?? "")}</p>`;
      }
    })
    .join("");
  return `<div class="diff">${title}${blocks}</div>`;
}

/** True when the two revisions differ in any way this diff would show. */
export function hasChanges(input: DiffInput): boolean {
  return input.before !== input.after || input.beforeTitle !== input.afterTitle;
}
